import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, EmptyState } from '@/components/ui-bits';
import { SlidersHorizontal, Plus, X, Save, Trash2, Eye, EyeOff, GripVertical, RotateCcw, LayoutTemplate } from 'lucide-react';
import { toast } from 'sonner';
import { SYSTEM_FIELD_META } from '@/lib/fields';
import { ROLE_LABELS } from '@/lib/roles';

const TYPES = ['text', 'textarea', 'number', 'currency', 'email', 'phone', 'select', 'multiselect', 'checkbox', 'radio', 'boolean', 'date', 'datetime', 'tags', 'file'];

const SYSTEM_FIELDS = Object.entries(SYSTEM_FIELD_META).map(([key, meta]) => ({
  key,
  defaultLabel: meta.defaultLabel,
  type: meta.type,
  defaultOrder: meta.defaultOrder,
  defaultListVisible: meta.defaultListVisible,
}));

const TAB_LABELS = {
  timeline: 'Timeline',
  notes: 'Notas',
  tasks: 'Tareas',
  whatsapp: 'WhatsApp',
  email: 'Email',
  checklist: 'Checklist',
};

const EXTRA_SYS_LABELS = {
  deal_value: 'Valor deal',
  company: 'Empresa',
  lost_reason: 'Motivo pérdida',
  status: 'Estado',
  stage_id: 'Etapa',
  assigned_to: 'Asignado',
  pipeline_id: 'Pipeline',
  tags: 'Tags',
  expected_close_at: 'Cierre esperado',
  marketing_opt_out: 'Opt-out marketing',
};

export default function CustomFieldsPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [systemFields, setSystemFields] = useState({});
  const [savingSystem, setSavingSystem] = useState(false);
  const [layoutMeta, setLayoutMeta] = useState(null);
  const [layouts, setLayouts] = useState({});
  const [layoutRole, setLayoutRole] = useState('ventas');
  const [savingLayout, setSavingLayout] = useState(false);

  const load = async () => {
    const [c, s] = await Promise.all([api.get('/custom-fields'), api.get('/admin/settings')]);
    setItems(c.data.fields);
    const sf = s.data.tenant?.settings?.system_fields || {};
    // backwards compat: pull labels from field_labels into system_fields
    const legacy = s.data.tenant?.settings?.field_labels || {};
    const merged = { ...sf };
    Object.entries(legacy).forEach(([k, v]) => { if (!merged[k]) merged[k] = { label: v }; });
    setSystemFields(merged);
    try {
      const { data: L } = await api.get('/admin/layouts');
      setLayoutMeta(L);
      setLayouts(L.layouts || {});
    } catch {
      setLayoutMeta(null);
    }
  };
  useEffect(() => { load(); }, []);

  const newField = () => setEditing({ key: '', label: '', type: 'text', required: false, options: [], order: 100 + items.length * 10, hidden: false, show_in_list: false });
  const save = async () => {
    try {
      if (editing.id) await api.patch(`/custom-fields/${editing.id}`, editing);
      else await api.post('/custom-fields', editing);
      toast.success('Guardado'); setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };
  const del = async (f) => { if (!confirm('Borrar?')) return; await api.delete(`/custom-fields/${f.id}`); load(); };

  const updateSys = (key, patch) => {
    setSystemFields((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
  };
  const resetSys = (key) => {
    setSystemFields((prev) => { const next = { ...prev }; delete next[key]; return next; });
  };
  const saveSystem = async () => {
    setSavingSystem(true);
    try { await api.patch('/admin/settings', { system_fields: systemFields }); toast.success('Configuración de campos del sistema guardada'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setSavingSystem(false); }
  };

  const curLayout = layouts[layoutRole] || {
    hidden_system_fields: [],
    hidden_custom_fields: [],
    readonly_system_fields: [],
    tabs: layoutMeta?.default_tabs || Object.keys(TAB_LABELS),
    show_deal_value: true,
    show_company: true,
  };

  const patchLayout = (patch) => {
    setLayouts((prev) => ({
      ...prev,
      [layoutRole]: { ...(prev[layoutRole] || curLayout), ...patch },
    }));
  };

  const toggleInList = (listKey, key) => {
    const list = new Set(curLayout[listKey] || []);
    if (list.has(key)) list.delete(key);
    else list.add(key);
    patchLayout({ [listKey]: [...list] });
  };

  const toggleTab = (tab) => {
    const tabs = [...(curLayout.tabs || [])];
    const i = tabs.indexOf(tab);
    if (i >= 0) {
      if (tabs.length <= 1) return toast.error('Debe quedar al menos una pestaña');
      tabs.splice(i, 1);
    } else {
      tabs.push(tab);
    }
    // keep default order
    const order = layoutMeta?.default_tabs || Object.keys(TAB_LABELS);
    patchLayout({ tabs: order.filter((t) => tabs.includes(t)) });
  };

  const saveLayouts = async () => {
    setSavingLayout(true);
    try {
      await api.patch('/admin/settings', { role_layouts: layouts });
      toast.success('Layouts por rol guardados');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setSavingLayout(false);
    }
  };

  return (
    <div>
      <PageHeader title="Campos personalizados" description="Personaliza todos los campos del lead: ocúltalos, renómbralos, marca cuáles son requeridos, y agrega nuevos." actions={<button data-testid="cf-new" onClick={newField} className="btn-primary"><Plus className="h-4 w-4" /> Nuevo campo personalizado</button>} />

      {/* SYSTEM FIELDS */}
      <div className="surface p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display font-semibold flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" /> Campos del sistema</h3>
            <p className="text-xs text-muted-foreground mt-1">Estos campos son parte del modelo base del lead. Puedes ocultarlos, renombrarlos, marcarlos requeridos y reordenarlos. Si los ocultas, dejarán de aparecer en formularios, API y tablas.</p>
          </div>
          <button data-testid="system-fields-save" onClick={saveSystem} disabled={savingSystem} className="btn-primary shrink-0"><Save className="h-4 w-4" /> {savingSystem ? 'Guardando…' : 'Guardar'}</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="px-2 py-2 font-medium">Campo (key)</th>
                <th className="px-2 py-2 font-medium">Etiqueta visible</th>
                <th className="px-2 py-2 font-medium">Placeholder</th>
                <th className="px-2 py-2 font-medium text-center">Visible</th>
                <th className="px-2 py-2 font-medium text-center">Requerido</th>
                <th className="px-2 py-2 font-medium text-center">En lista</th>
                <th className="px-2 py-2 font-medium text-center">Orden</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {SYSTEM_FIELDS.map((f) => {
                const cfg = systemFields[f.key] || {};
                const hidden = !!cfg.hidden;
                const required = cfg.required ?? false;
                const show_in_list = cfg.show_in_list ?? (['first_name', 'phone_e164', 'email', 'source'].includes(f.key));
                return (
                  <tr key={f.key} className="border-b border-border/70">
                    <td className="px-2 py-1.5">
                      <div className="text-sm font-medium">{f.defaultLabel}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">{f.key}</div>
                    </td>
                    <td className="px-2 py-1.5"><input data-testid={`sys-label-${f.key}`} value={cfg.label ?? ''} onChange={(e) => updateSys(f.key, { label: e.target.value })} placeholder={f.defaultLabel} className="input-sm" /></td>
                    <td className="px-2 py-1.5"><input value={cfg.placeholder ?? ''} onChange={(e) => updateSys(f.key, { placeholder: e.target.value })} placeholder="—" className="input-sm" /></td>
                    <td className="px-2 py-1.5 text-center">
                      <button data-testid={`sys-visible-${f.key}`} onClick={() => updateSys(f.key, { hidden: !hidden })} className={`btn-ghost h-7 w-7 p-0 ${hidden ? 'text-[hsl(var(--danger))]' : 'text-[hsl(var(--success))]'}`} title={hidden ? 'Oculto' : 'Visible'}>
                        {hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-2 py-1.5 text-center"><input type="checkbox" checked={!!required} onChange={(e) => updateSys(f.key, { required: e.target.checked })} /></td>
                    <td className="px-2 py-1.5 text-center"><input type="checkbox" checked={!!show_in_list} onChange={(e) => updateSys(f.key, { show_in_list: e.target.checked })} /></td>
                    <td className="px-2 py-1.5 text-center"><input type="number" value={cfg.order ?? f.defaultOrder} onChange={(e) => updateSys(f.key, { order: Number(e.target.value) })} className="input-sm w-16 text-center" /></td>
                    <td className="px-2 py-1.5 text-right"><button onClick={() => resetSys(f.key)} className="btn-ghost h-7 w-7 p-0" title="Restablecer"><RotateCcw className="h-3 w-3" /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ROLE LAYOUTS */}
      {layoutMeta && (
        <div className="surface p-5 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div>
              <h3 className="font-display font-semibold flex items-center gap-2">
                <LayoutTemplate className="h-4 w-4" /> Layouts por rol
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                Controla qué campos y pestañas ve cada rol en la ficha del lead (además de los campos del sistema ocultos globalmente).
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select className="input w-auto" value={layoutRole} onChange={(e) => setLayoutRole(e.target.value)}>
                {(layoutMeta.roles || []).map((r) => (
                  <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                ))}
              </select>
              <button type="button" className="btn-primary" disabled={savingLayout} onClick={saveLayouts}>
                <Save className="h-4 w-4" /> {savingLayout ? 'Guardando…' : 'Guardar layout'}
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-2">Campos del sistema ocultos para este rol</div>
              <div className="space-y-1 max-h-56 overflow-y-auto border border-border rounded-[10px] p-2">
                {(layoutMeta.system_keys || []).map((key) => {
                  const label = SYSTEM_FIELD_META[key]?.defaultLabel || EXTRA_SYS_LABELS[key] || key;
                  const hidden = (curLayout.hidden_system_fields || []).includes(key);
                  return (
                    <label key={key} className="flex items-center gap-2 text-xs py-0.5">
                      <input type="checkbox" checked={hidden} onChange={() => toggleInList('hidden_system_fields', key)} />
                      <span className={hidden ? 'text-muted-foreground line-through' : ''}>{label}</span>
                      <span className="font-mono text-[10px] text-muted-foreground">{key}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-2">Campos personalizados ocultos</div>
              <div className="space-y-1 max-h-56 overflow-y-auto border border-border rounded-[10px] p-2">
                {(layoutMeta.custom_fields || []).length === 0 && (
                  <p className="text-xs text-muted-foreground">Sin campos personalizados</p>
                )}
                {(layoutMeta.custom_fields || []).map((cf) => {
                  const hidden = (curLayout.hidden_custom_fields || []).includes(cf.key);
                  return (
                    <label key={cf.key} className="flex items-center gap-2 text-xs py-0.5">
                      <input type="checkbox" checked={hidden} onChange={() => toggleInList('hidden_custom_fields', cf.key)} />
                      <span className={hidden ? 'text-muted-foreground line-through' : ''}>{cf.label}</span>
                    </label>
                  );
                })}
              </div>
              <div className="mt-3 space-y-1">
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={curLayout.show_deal_value !== false}
                    onChange={(e) => patchLayout({ show_deal_value: e.target.checked })}
                  />
                  Mostrar valor deal
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={curLayout.show_company !== false}
                    onChange={(e) => patchLayout({ show_company: e.target.checked })}
                  />
                  Mostrar empresa
                </label>
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-2">Pestañas visibles en ficha</div>
              <div className="space-y-1 border border-border rounded-[10px] p-2">
                {(layoutMeta.default_tabs || Object.keys(TAB_LABELS)).map((tab) => {
                  const on = (curLayout.tabs || []).includes(tab);
                  return (
                    <label key={tab} className="flex items-center gap-2 text-xs py-0.5">
                      <input type="checkbox" checked={on} onChange={() => toggleTab(tab)} />
                      {TAB_LABELS[tab] || tab}
                    </label>
                  );
                })}
              </div>
              <div className="mt-3">
                <div className="text-xs font-medium text-muted-foreground mb-1">Solo lectura (sistema)</div>
                <div className="space-y-1 max-h-28 overflow-y-auto border border-border rounded-[10px] p-2">
                  {['deal_value', 'status', 'stage_id', 'assigned_to', 'pipeline_id'].map((key) => (
                    <label key={key} className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={(curLayout.readonly_system_fields || []).includes(key)}
                        onChange={() => toggleInList('readonly_system_fields', key)}
                      />
                      {EXTRA_SYS_LABELS[key] || key}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CUSTOM FIELDS */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-semibold">Campos personalizados (creados por tu empresa)</h3>
      </div>
      {items.length === 0 ? (
        <EmptyState icon={SlidersHorizontal} title="Sin campos personalizados" description="Agrega los campos que tu empresa necesita: presupuesto, ciudad, tipo de crédito, etc." cta={<button onClick={newField} className="btn-primary">Nuevo campo</button>} />
      ) : (
        <div className="surface">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="px-3 py-2">Etiqueta</th><th className="px-3 py-2">Key</th><th className="px-3 py-2">Tipo</th><th className="px-3 py-2">Requerido</th><th className="px-3 py-2">En lista</th><th className="px-3 py-2">Orden</th><th></th></tr></thead>
            <tbody>{items.map((f) => (
              <tr key={f.id} data-testid="cf-row" className="h-10 border-b border-border/70 hover:bg-accent/60">
                <td className="px-3">{f.label} {f.hidden && <EyeOff className="inline h-3 w-3 text-muted-foreground" />}</td>
                <td className="px-3 font-mono text-xs text-muted-foreground">{f.key}</td>
                <td className="px-3"><span className="chip">{f.type}</span></td>
                <td className="px-3">{f.required ? 'Sí' : 'No'}</td>
                <td className="px-3">{f.show_in_list ? <Eye className="h-4 w-4 text-[hsl(var(--success))]" /> : <span className="text-muted-foreground">—</span>}</td>
                <td className="px-3">{f.order}</td>
                <td className="px-3 text-right"><button onClick={() => setEditing(f)} className="btn-ghost h-7 px-2 text-xs">Editar</button><button onClick={() => del(f)} className="btn-ghost h-7 w-7 p-0 text-[hsl(var(--danger))]"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="surface w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4"><h2 className="font-display text-xl font-semibold">Campo personalizado</h2><button onClick={() => setEditing(null)} className="btn-ghost h-8 w-8 p-0"><X className="h-4 w-4" /></button></div>
            <div className="space-y-3">
              <label>Etiqueta<input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} className="input mt-1" /></label>
              <label>Key (sin espacios)<input value={editing.key} onChange={(e) => setEditing({ ...editing, key: e.target.value.replace(/\s+/g, '_').toLowerCase() })} className="input mt-1 font-mono text-xs" disabled={!!editing.id} /></label>
              <label>Tipo<select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} className="input mt-1">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></label>
              {(editing.type === 'select' || editing.type === 'multiselect' || editing.type === 'radio') && (
                <label>Opciones (separadas por coma)<input value={(editing.options || []).join(', ')} onChange={(e) => setEditing({ ...editing, options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} className="input mt-1" /></label>
              )}
              <label>Orden<input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} className="input mt-1" /></label>
              <label>Placeholder<input value={editing.placeholder || ''} onChange={(e) => setEditing({ ...editing, placeholder: e.target.value })} className="input mt-1" /></label>
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.required} onChange={(e) => setEditing({ ...editing, required: e.target.checked })} /> Requerido</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.hidden} onChange={(e) => setEditing({ ...editing, hidden: e.target.checked })} /> Oculto en formulario</label>
                <label className="flex items-center gap-2 text-sm"><input data-testid="cf-show-in-list" type="checkbox" checked={!!editing.show_in_list} onChange={(e) => setEditing({ ...editing, show_in_list: e.target.checked })} /> Mostrar como columna en lista</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4"><button onClick={() => setEditing(null)} className="btn-ghost">Cancelar</button><button data-testid="cf-save" onClick={save} className="btn-primary"><Save className="h-4 w-4" /> Guardar</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
